/**
 * Financement : hausses de recettes (Md€ en régime, avec courbe de fuite), rabot transversal, économies ciblées par mission,
 * coût politique. Tous les coefficients viennent de params (`lev_<id>_*`, `cut_*`, `lev_save_*`, `leak_cap`). Coût politique :
 * règle symétrique unique (engine/src/politics.ts, ADR 0009) — les anciens `lev_<id>_political_cost_per_bn`,
 * `lev_tax_cut_political_gain_per_bn` et `cut_political_cost_per_pt` ne sont plus lus.
 */
import { param, paramOpt } from "./params.js";
import { leverIncidence, measureIncidence, measurePoliticalCost, missionIncidence, politicalCostOfBn } from "./politics.js";
import { applyVariant, PCT } from "./state.js";
import { hypTarget, profileSteady } from "./profile.js";
import type { Financing, Measure, ParamSet, Programme } from "./types.js";

export interface LeverEffect {
  id: string;
  /** Hausse affichée (Md€, arithmétique du tract). */
  raw: number;
  /** Part perdue par fuite/évitement (0–1). */
  leak: number;
  /** Recette effective après fuite (Md€). */
  net: number;
  /** Frein à l'activité (Md€ de demande retirés, = multiplicateur × brut). */
  tax_drag: number;
  political_cost: number;
  poverty_pt: number;
  /** Facteur multiplicatif sur les émissions (1 = neutre). */
  co2_factor: number;
}

/**
 * Part perdue par réaction comportementale de l'assiette (ADR 0017). `raise` en Md€ (> 0 hausse, < 0 baisse), `yieldBn` = produit
 * actuel de l'impôt (Md€) ; le levier est un changement proportionnel du taux : x = raise / yieldBn.
 * - Impôt ad valorem (`lev_<id>_base_elasticity` ε à (1 − τ), `lev_<id>_marginal_rate` τ₀) : assiette × ((1 − τ₀(1 + x)) / (1 − τ₀))^ε,
 *   d'où ΔR = T₀ [(1 + x) · ratio − 1] ; au premier ordre ΔR = raise × (1 − ε τ₀ / (1 − τ₀)) dans les deux sens.
 * - Accise (`lev_<id>_tax_share_of_price` s présent, ε = élasticité-prix de la demande en valeur absolue) : quantités × (1 + s x)^(−ε).
 * Sans produit connu (fixtures) : approximation du premier ordre (part constante, antisymétrique exacte).
 * Sans élasticité sourcée : courbe conventionnelle `lev_<id>_leak_per_bn` × |Md€|, appliquée dans les deux sens (symétrie).
 */
export function behaviouralLeak(params: ParamSet, id: string, raise: number, yieldBn?: number): number {
  if (raise === 0) return 0;
  const eps = paramOpt(params, `lev_${id}_base_elasticity`);
  if (eps === undefined) return Math.min(param(params, "leak_cap"), param(params, `lev_${id}_leak_per_bn`) * Math.abs(raise));
  const share = paramOpt(params, `lev_${id}_tax_share_of_price`);
  if (share !== undefined) {
    if (yieldBn === undefined || yieldBn <= 0) return eps * share;
    const x = raise / yieldBn;
    const net = yieldBn * ((1 + x) * Math.pow(Math.max(1e-9, 1 + share * x), -eps) - 1);
    return 1 - net / raise;
  }
  const tau = param(params, `lev_${id}_marginal_rate`);
  if (yieldBn === undefined || yieldBn <= 0) return (eps * tau) / (1 - tau);
  const x = raise / yieldBn;
  const ratio = Math.pow(Math.max(0, 1 - tau * (1 + x)) / (1 - tau), eps);
  const net = yieldBn * ((1 + x) * ratio - 1);
  return 1 - net / raise;
}

export function leverEffect(params: ParamSet, id: string, raise: number, yieldBn?: number): LeverEffect {
  const leak = behaviouralLeak(params, id, raise, yieldBn);
  const net = raise * (1 - leak);
  const mult = param(params, `lev_${id}_tax_drag_mult`);
  // Règle symétrique (ADR 0009) : une hausse de recette est un resserrement (montant « coût » = −hausse).
  const pol = politicalCostOfBn(params, -raise, leverIncidence(id));
  const povPerBn = paramOpt(params, `lev_${id}_poverty_per_bn`) ?? 0;
  const co2PerBn = paramOpt(params, `lev_${id}_co2_per_bn`) ?? 0;
  return {
    id,
    raw: raise,
    leak,
    net,
    tax_drag: mult * raise,
    political_cost: pol,
    poverty_pt: raise > 0 ? povPerBn * raise : 0,
    co2_factor: 1 + co2PerBn * raise,
  };
}

export interface CutEffect {
  /** Rabot en points (%) des crédits des missions. */
  pts: number;
  /** Économie affichée (Md€) = part × crédits tendanciels. */
  raw: number;
  /** Économie effective après perte d'efficacité. */
  net: number;
  tax_drag: number;
  political_cost: number;
  /** Écart de NIVEAU des indices de service de chaque mission tant que le rabot est en vigueur (ADR 0009 : plus cumulatif). */
  svc_shift: number;
}

export function cutEffect(params: ParamSet, share: number, missionsSpend: number): CutEffect {
  const pts = share * PCT;
  const raw = share * missionsSpend;
  const net = raw * Math.max(0, 1 - param(params, "cut_efficiency_loss_per_pt") * pts);
  return {
    pts,
    raw,
    net,
    tax_drag: param(params, "cut_tax_drag_mult") * net,
    // Même règle que tout le reste, sur l'économie affichée (ADR 0009) ; incidence « services publics ».
    political_cost: politicalCostOfBn(params, -raw, "public"),
    svc_shift: -param(params, "cut_svc_loss_per_pt") * pts,
  };
}

/** Économie ciblée sur une mission (levier d'économie, symétrique d'un levier de recette — ADR 0009). */
export interface SavingsEffect {
  mission: string;
  /** % des crédits tendanciels du groupe (négatif = hausse). */
  pct: number;
  /** Économie affichée (Md€) = part × crédits tendanciels de la mission. */
  raw: number;
  /** Part perdue (reports de charges, dépenses contraintes) : même courbe que les fuites des leviers de recettes. */
  leak: number;
  net: number;
  tax_drag: number;
  political_cost: number;
  /** Écart de niveau de l'indice de service de la mission (pt d'indice). */
  svc_shift: number;
  /** Δ % du revenu disponible des ménages (part de la dépense qui leur parvient, `pp_transfer_share_mission_<id>`). */
  income_pct: number;
}

/** `pct` : % des crédits tendanciels du groupe (négatif = hausse des crédits). */
export function savingsEffect(params: ParamSet, mission: string, pct: number, missionCredits: number): SavingsEffect {
  const share = pct / PCT;
  const raw = share * missionCredits;
  // Symétrique (ADR 0016) : une hausse de crédits est elle aussi partiellement non consommée (sous-exécution), même courbe.
  const leak = Math.min(param(params, "leak_cap"), param(params, "lev_save_leak_per_bn") * Math.abs(raw));
  const net = raw * (1 - leak);
  const ppShare = paramOpt(params, `pp_transfer_share_mission_${mission}`) ?? 0;
  return {
    mission,
    pct,
    raw,
    leak,
    net,
    tax_drag: param(params, "cut_tax_drag_mult") * net,
    political_cost: politicalCostOfBn(params, -raw, missionIncidence(params, mission)),
    // Une économie porte soit sur des transferts aux ménages (revenu), soit sur des services (indice) : la part `ppShare` passe par
    // le revenu, le reste par l'indice de service — jamais les deux pour le même euro (ADR 0009 §2).
    svc_shift: -param(params, "cut_svc_loss_per_pt") * share * PCT * (1 - ppShare),
    income_pct: (-raw * ppShare * PCT) / param(params, "pp_household_disposable_income_bn"),
  };
}

export interface FinancingPreview {
  levers: LeverEffect[];
  cut: CutEffect;
  /** Économies ciblées par mission (vide sans `missionCredits`). */
  savings: SavingsEffect[];
  /** Total affiché (Md€/an). */
  static_total: number;
  /** Total après fuite et perte d'efficacité (Md€/an). */
  net_total: number;
  tax_drag: number;
  /** Coût annuel en capital politique du financement. */
  political_cost: number;
}

/**
 * Aperçu du financement pour une dépense tendancielle des missions donnée (Md€). `missionCredits` (id → crédits tendanciels de
 * l'année, Md€) est requis pour chiffrer les économies ciblées par mission ; sans lui elles sont ignorées.
 */
export function previewFinancing(
  params: ParamSet,
  financing: Financing,
  missionsSpend: number,
  missionCredits?: Record<string, number>,
  revenueYields?: Record<string, number>,
): FinancingPreview {
  const levers = Object.entries(financing.revenue_raises)
    .filter(([, v]) => v !== 0)
    .map(([id, v]) => leverEffect(params, id, v, revenueYields?.[id]));
  const cut = cutEffect(params, financing.across_the_board_cut, missionsSpend);
  const savings = missionCredits
    ? Object.entries(financing.mission_cuts ?? {})
        .filter(([, v]) => v !== 0)
        .map(([id, v]) => {
          const c = missionCredits[id];
          if (c === undefined) throw new Error(`financing : économie ciblée sur une mission inconnue « ${id} »`);
          return savingsEffect(params, id, v, c);
        })
    : [];
  const sum = (f: (x: { raw: number; net: number; tax_drag: number; political_cost: number }) => number): number =>
    levers.reduce((a, l) => a + f(l), 0) + savings.reduce((a, l) => a + f(l), 0) + f(cut);
  return {
    levers,
    cut,
    savings,
    static_total: sum((x) => x.raw),
    net_total: sum((x) => x.net),
    tax_drag: sum((x) => x.tax_drag),
    political_cost: sum((x) => x.political_cost),
  };
}

/**
 * Coût politique (capital consommé ; < 0 = gagné) d'un montant donné, par la règle symétrique unique (ADR 0009), dans la convention
 * de signe de chaque instrument : `lever` → `amountBn` = hausse de recette (> 0) ou baisse (< 0) du levier `id` ; `mission_cut`
 * → `amountBn` = économie (> 0) ou hausse de crédits (< 0) sur le groupe `id` ; `cut` → économie du rabot transversal ;
 * `measure` → coût net de la mesure (> 0 = dépense ou baisse d'impôt), `id` = catégorie de mesure ; `crisis` → coût de l'option.
 * Coût par Md€ affichable = `politicalCostPerBn(params, kind, id, 1)` (ou −1 pour l'autre sens).
 */
export function politicalCostPerBn(params: ParamSet, kind: "lever" | "mission_cut" | "cut" | "measure" | "crisis", id: string, amountBn: number): number {
  switch (kind) {
    case "lever":
      return politicalCostOfBn(params, -amountBn, leverIncidence(id));
    case "mission_cut":
      return politicalCostOfBn(params, -amountBn, missionIncidence(params, id));
    case "cut":
      return politicalCostOfBn(params, -amountBn, "public");
    case "measure":
      return politicalCostOfBn(params, amountBn, measureIncidence(params, { category: id as Measure["category"] }));
    case "crisis":
      return politicalCostOfBn(params, amountBn, "public");
  }
}

/** Coût net d'une mesure en régime (Md€/an, > 0 = dépense), curseur d'hypothèse appliqué. */
export function measureSteadyCost(m: Measure, hyp: number | undefined): number {
  const c = profileSteady(m.cost);
  return hypTarget(m) === "cost_leak" && hyp !== undefined ? c * (1 - hyp / PCT) : c;
}

/** Besoin de financement net du programme en régime de croisière (Md€/an, > 0 = à financer). */
export function needGap(programme: Programme, catalogue: Measure[]): number {
  let t = 0;
  for (const id of programme.measure_ids) {
    const found = catalogue.find((x) => x.id === id);
    if (!found) throw new Error(`needGap : mesure inconnue « ${id} »`);
    const v = programme.variants?.[id];
    const m = v !== undefined ? applyVariant(found, v) : found;
    t += measureSteadyCost(m, programme.hyp[id] ?? m.hyp?.default);
  }
  return t;
}

/**
 * Coût politique ANNUEL du programme en régime (règle symétrique, ADR 0009). v0.2 : coût payé une fois à l'adoption ; depuis la
 * v0.3 le coût d'une mesure est un flux annuel, comme celui des leviers (même règle, même unité). Nom conservé pour l'UI.
 */
export function adoptionPoliticalCost(params: ParamSet, measures: Measure[], hyp: Record<string, number> = {}): number {
  return measures.reduce((a, m) => a + measurePoliticalCost(params, m, hyp[m.id]), 0);
}
